import { IMapperType }           from '@/Emulator';
import { Cartridge, MirrorMode } from '@/Emulator/IO';
import { IMapper }               from '@/Emulator/Mapper/IMapper';
import { StaticImplements }      from '@/LanguageUtilities';

/**
 * Mapper ID 0, also known as NROM.
 *
 * The generic designation NROM refers to the Nintendo cartridge boards
 * NES-NROM-128, NES-NROM-256, their HVC counterparts, and clone boards.
 */
@StaticImplements<IMapperType>()
export class Mapper000 implements IMapper
{
    /**
     * @see {@link IMapperType.supports}
     */
    public static supports(cartridge: Cartridge): boolean
    {
        return cartridge.mapperId === 0;
    }

    /**
     * The NES CPU allocates a 32KB window ($8000-$FFFF) strictly for the PRG
     * ROM. When a cartridge only contains 16KB of PRG data, placing it at the
     * start of this window leaves the upper half empty.
     *
     * Because the 6502 CPU is hardwired to look at the very end of its
     * addressable memory ($FFFC-$FFFD) for the reset vector, this mask ensures
     * the 16KB PRG ROM is mirrored across the entire 32KB window, guaranteeing
     * the CPU can successfully boot.
     */
    private readonly prgMask: 0x3FFF | 0x7FFF;

    constructor(private readonly cartridge: Cartridge)
    {
        this.prgMask = this.cartridge.prgRom.length > 16384 ? 0x7FFF : 0x3FFF;
    }

    /**
     * @inheritDoc
     */
    public peekCpu(address: number): number
    {
        return this.readCpu(address);
    }

    /**
     * @inheritDoc
     */
    public readCpu(address: number): number
    {
        return address >= 0x8000
            ? this.cartridge.prgRom[address & this.prgMask]
            : 0;
    }

    /**
     * @inheritDoc
     */
    public peekPpu(address: number): number
    {
        return this.readPpu(address);
    }

    /**
     * @inheritDoc
     */
    public readPpu(address: number): number
    {
        return address <= 0x1FFF
            ? this.cartridge.chrData[address]
            : 0;
    }

    /**
     * @inheritDoc
     */
    public writeCpu(address: number, value: number): void
    {
        // NROM cartridges have no hardware registers to switch banks, so we'll
        // ignore all writes to the CPU address space.
    }

    /**
     * @inheritDoc
     */
    public writePpu(address: number, value: number): void
    {
        if (this.cartridge.hasChrRam && address <= 0x1FFF) {
            this.cartridge.chrData[address] = value & 0xFF; // Enforce 8bit integer.
        }
    }

    /**
     * @inheritDoc
     */
    public get hasPendingInterruptRequest(): boolean
    {
        return false;
    };

    /**
     * @inheritDoc
     */
    public get mirrorMode(): MirrorMode
    {
        return this.cartridge.mirrorMode;
    }

    /**
     * @inheritDoc
     */
    public serialize(): Record<string, any>
    {
        return {};
    }

    /**
     * @inheritDoc
     */
    public deserialize(data: Record<string, any>): void
    {
    }
}
